import { useQuery } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { bookingApi } from '@/features/booking/api/booking.api';
import { Button } from '@/shared/components/ui';

interface CancelBookingDialogProps {
  bookingId: string | null;
  open: boolean;
  cancelPending?: boolean;
  onClose: () => void;
  onConfirm: (bookingId: string) => void;
}

function formatMoney(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

export function CancelBookingDialog({
  bookingId,
  open,
  cancelPending,
  onClose,
  onConfirm,
}: CancelBookingDialogProps) {
  const quoteQuery = useQuery({
    queryKey: ['bookings', bookingId, 'cancellation-quote'],
    queryFn: () => bookingApi.getCancellationQuote(bookingId!),
    enabled: open && Boolean(bookingId),
  });

  const quote = quoteQuery.data;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Cancel booking?</DialogTitle>
      <DialogContent>
        {quoteQuery.isLoading && (
          <CircularProgress size={28} sx={{ display: 'block', mx: 'auto', my: 2 }} />
        )}
        {quoteQuery.isError && (
          <Alert severity="error">Could not load cancellation details. Please try again.</Alert>
        )}
        {quote && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {quote.message}
            </Typography>
            {quote.allowed ? (
              <Alert severity={quote.refundPercent === 100 ? 'success' : 'warning'}>
                {quote.tier === 'pending_unpaid'
                  ? 'No payment has been captured, so nothing will be refunded.'
                  : `Estimated refund: ${formatMoney(quote.refundAmount, quote.currency)} (${quote.refundPercent}%).`}
                {(quote.extensionAmount ?? 0) > 0 &&
                  ` Paid extra time: ${formatMoney(quote.extensionRefundAmount ?? 0, quote.currency)} of ${formatMoney(
                    quote.extensionAmount ?? 0,
                    quote.currency
                  )} refunded at the same rate.`}
              </Alert>
            ) : (
              <Alert severity="error">This booking cannot be cancelled right now.</Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button intent="secondary-outlined" onClick={onClose} disabled={cancelPending}>
          Keep booking
        </Button>
        <Button
          intent="primary-dark"
          disabled={!quote?.allowed || cancelPending || quoteQuery.isLoading}
          onClick={() => bookingId && onConfirm(bookingId)}
        >
          {cancelPending ? 'Cancelling…' : 'Confirm cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
